import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Paper,
  Tooltip
} from '@mui/material';
import {
  AddPhotoAlternate as UploadIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { toPng } from 'html-to-image';

// Define font options
const fontOptions = [
  { value: 'system-ui', label: 'System' },
  { value: 'Inter, sans-serif', label: 'Inter' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Playfair Display, serif', label: 'Playfair' },
  { value: 'Montserrat, sans-serif', label: 'Montserrat' },
  { value: 'Roboto, sans-serif', label: 'Roboto' }
];

// Define logo positions
const logoPositions = [
  { value: 'top-right', label: 'Top Right' },
  { value: 'top-left', label: 'Top Left' },
  { value: 'bottom-right', label: 'Bottom Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'none', label: 'No Logo' }
];

// Statement Preview component
const StatementPreview = ({ 
  statementText, 
  statementSize,
  bgColor,
  patternColor,
  textColor,
  selectedStyle,
  customBgImage,
  previewRef,
  sizes,
  backgroundStyles,
  selectedFont
}) => {
  const formatStatementText = (text) => {
    if (!text) return 'Your statement will appear here...';
    return text;
  };

  const currentSize = sizes.find(s => s.value === statementSize) || sizes[0];
  const style = backgroundStyles.find(s => s.value === selectedStyle);
  
  const getBackgroundStyles = () => {
    if (selectedStyle === 'custom-image' && customBgImage) {
      return {
        backgroundImage: `url("${customBgImage}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: bgColor
      };
    }
    if (style && style.preview) {
      return style.preview(bgColor, patternColor);
    }
    return { backgroundColor: bgColor };
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        ...getBackgroundStyles(),
        color: textColor
      }}
    >
      <Box
        ref={previewRef}
        className="statement-preview-container"
        sx={{
          position: 'relative',
          width: '90%',
          maxWidth: '550px',
          aspectRatio: `${currentSize.aspect}`,
          borderRadius: '8px',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Main Statement Text */}
        <Box
          sx={{ 
            width: '85%',
            textAlign: 'center',
            fontFamily: selectedFont,
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: '-0.015em',
            padding: 2,
            '& p': { margin: 0 },
            '& strong, & b': { color: patternColor },
            '& .ql-size-small': { fontSize: '0.75em' },
            '& .ql-size-large': { fontSize: '1.25em' },
            '& .ql-size-huge': { fontSize: '1.5em' },
          }}
          dangerouslySetInnerHTML={{ __html: formatStatementText(statementText) }}
        />
      </Box>
    </Box>
  );
};

const BoldStatementGenerator = forwardRef(({
  onSaveToPost, 
  onStateChange, 
  showPreview = false, 
  previewOnly = false,
  hideEditor = false,
  onTextChange,
  statementText: propStatementText = '<p>Users can\'t trust<br><b>Google.</b></p>', 
  statementSize: propStatementSize = 'square',
  bgColor: propBgColor,
  patternColor: propPatternColor,
  textColor: propTextColor,
  selectedStyle: propSelectedStyle,
  customBgImage: propCustomBgImage = null,
  selectedFont: propSelectedFont,
  sizes,
  backgroundStyles
}, ref) => {
  const [statementText, setStatementText] = useState(propStatementText);
  const [forceRender, setForceRender] = useState(0);
  const [downloading, setDownloading] = useState(false);

  const containerRef = useRef(null);
  const internalPreviewRef = useRef(null);
  const activePreviewRef = internalPreviewRef;

  // Update useEffect to include state changes notification
  useEffect(() => {
    if (onStateChange) {
      const currentState = {
        statementText,
        statementSize: propStatementSize,
        bgColor: propBgColor,
        patternColor: propPatternColor,
        textColor: propTextColor,
        selectedStyle: propSelectedStyle,
        customBgImage: propCustomBgImage,
        selectedFont: propSelectedFont
      };
      onStateChange(currentState);
    }
  }, [
    statementText,
    propStatementSize,
    propBgColor,
    propPatternColor,
    propTextColor,
    propSelectedStyle,
    propCustomBgImage,
    propSelectedFont,
    onStateChange
  ]);

  // Expose handleDownload and handleSaveToPost via the forwarded ref
  useImperativeHandle(ref, () => ({
    handleDownload,
    handleSaveToPost
  }));

  const handleDownload = async () => {
    try {
      setDownloading(true);
      
      // Try multiple ways to find the preview container
      let previewContainer = null;
      
      // First try using querySelector
      previewContainer = document.querySelector('.statement-preview-container');
      
      // If not found, try using activePreviewRef
      if (!previewContainer && activePreviewRef && activePreviewRef.current) {
        previewContainer = activePreviewRef.current;
      }
      
      if (!previewContainer) {
        throw new Error('Statement preview container not found');
      }
      
      // Generate the image using html-to-image
      const dataUrl = await toPng(previewContainer, {
        quality: 1.0,
        pixelRatio: 3, // Higher resolution
        cacheBust: true,
        skipFonts: false
      });
      
      // Create download link
      const link = document.createElement('a');
      link.download = `statement-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
      
    } catch (error) {
      console.error('Error generating statement image:', error);
      alert('There was an error generating the statement image. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleSaveToPost = async () => {
    try {
      // Try multiple ways to find the preview container
      let previewContainer = null;
      
      // First try using querySelector
      previewContainer = document.querySelector('.statement-preview-container');
      
      // If not found, try using activePreviewRef
      if (!previewContainer && activePreviewRef && activePreviewRef.current) {
        previewContainer = activePreviewRef.current;
      }
      
      if (!previewContainer) {
        throw new Error('Statement preview container not found');
      }
      
      // Generate the image using html-to-image
      const dataUrl = await toPng(previewContainer, {
        quality: 1.0,
        pixelRatio: 3, // Higher resolution
        cacheBust: true,
        skipFonts: false
      });
      
      // Convert data URL to blob
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      
      // Pass blob to parent component
      if (onSaveToPost) {
        onSaveToPost(blob);
      }
      
    } catch (error) {
      console.error('Error preparing statement image:', error);
      alert('There was an error preparing the statement image. Please try again.');
    }
  };

  // Render preview with current state
  const renderPreview = () => {
    return (
      <StatementPreview
        key={`preview-${propBgColor}-${propPatternColor}-${propSelectedStyle}-${propSelectedFont}-${forceRender}`}
        statementText={previewOnly ? propStatementText : statementText}
        statementSize={propStatementSize}
        bgColor={propBgColor}
        patternColor={propPatternColor}
        textColor={propTextColor}
        selectedStyle={propSelectedStyle}
        customBgImage={propCustomBgImage}
        previewRef={activePreviewRef}
        sizes={sizes}
        backgroundStyles={backgroundStyles}
        selectedFont={propSelectedFont}
      />
    );
  };

  // If previewOnly is true, only render the StatementPreview component
  if (previewOnly) {
    const currentSize = sizes.find(s => s.value === propStatementSize) || sizes[0];
    
    return (
      <Box ref={containerRef} sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0 }}>
        <Box sx={{
          width: '100%',
          maxWidth: '550px',
          height: 'auto',
          position: 'relative',
          '&::before': {
            content: '""',
            display: 'block',
            paddingTop: `${(currentSize.height / currentSize.width) * 100}%`,
          }
        }}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {renderPreview()}
          </Box>
        </Box>
      </Box>
    );
  }

  // Define Quill modules and formats 
  const quillModules = {
    toolbar: [
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline'],
      [{ 'align': [] }],
      [{ 'color': [] }],
      ['clean'] 
    ],
  };

  const quillFormats = [
    'size',
    'bold', 'italic', 'underline',
    'align',
    'color'
  ];

  return (
    <Box ref={containerRef} sx={{ display: 'flex', flexDirection: { xs: 'column', md: showPreview ? 'column' : 'row' }, gap: 2, width: '100%', height: '100%' }}>
      {/* Control Panel */}
      <Box sx={{
        flex: showPreview ? 1 : '1',
        display: 'flex',
        flexDirection: 'column',
        px: 1.5, pb: 1.5,
        borderRight: showPreview ? 'none' : '1px solid rgba(0,0,0,0.08)'
      }}>
        {/* Content area */}
        <Box sx={{ flex: 1, pr: 0.5 }}>
          {/* Statement Content */}
          {!hideEditor && (
            <>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  mb: 1.5, 
                  fontWeight: 600, 
                  color: '#4A5568',
                  fontSize: '0.95rem' 
                }}
              >
                Statement Content
              </Typography>
              
              {/* Rich Text Editor */}
              <Box sx={{ 
                mb: 3,
                '& .ql-toolbar': {
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  borderColor: 'rgba(0, 0, 0, 0.1)',
                  borderBottom: 'none'
                },
                '& .ql-container': {
                  borderBottomLeftRadius: '8px',
                  borderBottomRightRadius: '8px',
                  borderColor: 'rgba(0, 0, 0, 0.1)',
                  minHeight: '150px'
                },
                '& .ql-editor': {
                  minHeight: '150px',
                  padding: '12px 15px'
                }
              }}> 
                <ReactQuill 
                  theme="snow" 
                  value={statementText}
                  onChange={(value) => {
                    setStatementText(value);
                    if (onTextChange) {
                      onTextChange(value);
                    }
                  }}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter your bold statement here..."
                  className="statement-editor" 
                />
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Preview Panel - Only show if showPreview is true */}
      {showPreview && (
        <Box sx={{ flex: '1', display: 'flex', flexDirection: 'column', mt: 2, position: 'relative', width: showPreview ? '100%' : '50%' }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#4A5568', display: 'flex', alignItems: 'center', gap: 1 }}>
            Statement Preview
          </Typography>
          <Box sx={{ flex: 1, border: '1px solid rgba(0, 0, 0, 0.05)', borderRadius: 2, overflow: 'hidden', position: 'relative', height: '100%', minHeight: 300 }}>
            {renderPreview()}
          </Box>
        </Box>
      )}
    </Box>
  );
});

export default BoldStatementGenerator;